'use client';

import { useEffect } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import { Product } from './ProductCard';

interface QuickViewModalProps {
  product: Product | null;
  onClose: () => void;
  currentLang?: 'sl' | 'en';
}

export default function QuickViewModal({ product, onClose, currentLang = 'sl' }: QuickViewModalProps) {
  useEffect(() => {
    if (product) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [product]);

  if (!product) return null;

  const closeLabel = currentLang === 'sl' ? 'Zapri' : 'Close';

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-xl z-50 flex items-center justify-center p-6"
      onClick={onClose}
    >
      <div
        className="bg-card card-rounded max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="grid md:grid-cols-2 gap-8 p-8">
          {/* Image */}
          <div className="aspect-square card-rounded overflow-hidden bg-white">
            <AppImage
              src={product.image}
              alt={product.alt}
              className="w-full h-full object-contain"
            />
          </div>

          {/* Content */}
          <div className="flex flex-col justify-between">
            <div className="space-y-6">
              <div className="flex items-start justify-between">
                <div>
                  <span className="inline-block bg-accent text-accent-foreground px-3 py-1 rounded-full text-xs font-medium mb-3">
                    {product.category}
                  </span>
                  <h2 className="text-3xl font-bold">{product.name}</h2>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-muted rounded-full transition-colors"
                  aria-label={closeLabel}
                >
                  <Icon name="XMarkIcon" size={24} />
                </button>
              </div>

              <p className="text-lg text-muted-foreground leading-relaxed">
                {product.description}
              </p>

              <div className="text-4xl font-bold text-primary">{product.price}</div>
            </div>

            <div className="pt-6 border-t border-border">
              <p className="text-sm text-muted-foreground">
                {currentLang === 'sl' ?'Za nakup obiščite našo čebelarijo ali nas kontaktirajte.' :'Visit our beekeeping or contact us to purchase.'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}